import React from 'react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { theme } from '../../../../theme/theme'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { CreateUpdateRegisterEntranceItemForm } from '../CreateUpdateRegisterEntranceItemForm'
import { NSocietyEntrances } from '../../../../interfaces/services/society-entrances.interfaces'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { societyEntranceCreateSchema } from '../../../../forms/schemas/society_entrance_create'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { ISocietyEntranceModel } from '../../../../interfaces/models/society-entrance.interfaces'
import {
  createEntrance,
  patchEntrance,
} from '../../../../api/society-entrances'
import { i18nSocietyType } from '../../../../i18n'

interface CreateUpdateRegisterEntranceItemModalProps {
  show: boolean
  close: () => void
  entrance?: ISocietyEntranceModel
  children?: React.ReactNode
}

export const CreateUpdateRegisterEntranceItemModal = ({
  show,
  close,
  entrance,
  children,
}: CreateUpdateRegisterEntranceItemModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!entrance

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate(
        updateMode
          ? 'registerEntrance.flashMessage.updateEntranceSuccess'
          : 'registerEntrance.flashMessage.createEntranceSuccess',
        {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        }
      )
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate(
        updateMode
          ? 'registerEntrance.flashMessage.updateEntranceFailure'
          : 'registerEntrance.flashMessage.createEntranceFailure',
        {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        }
      )
    )
  }

  const { mutate: mutatePatchEntrance, isPending: isPatchingEntrance } =
    useMutation({
      mutationFn: patchEntrance,
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const { mutate: mutateCreateEntrance, isPending: isCreatingEntrance } =
    useMutation({
      mutationFn: createEntrance,
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyEntranceCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutatePatchEntrance({
        id: entrance._id,
        body: data as NSocietyEntrances.NPatch.IRequestBody,
      })
    } else {
      mutateCreateEntrance(data as NSocietyEntrances.NCreate.IRequestBody)
    }
  }

  const onError = (): void => {
    showErrorMessage()
  }

  const getModalTitle = (): string => {
    if (updateMode) {
      return translate('registerEntrance.modal.update', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      })
    }
    return translate('registerEntrance.modal.create', {
      context: i18nSocietyType(society?.societyTypeDisplayName),
    })
  }

  const loading = isCreatingEntrance || isPatchingEntrance

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              <div>
                <p style={theme.textVariants.h3}>{getModalTitle()}</p>
              </div>
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {society && (
                <CreateUpdateRegisterEntranceItemForm
                  society={society}
                  onError={onError}
                  onSubmit={onSubmit}
                  onClose={close}
                  loading={loading}
                  entrance={entrance}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
